import { ApiResult, caller, urlMaker, appendToFormData, extractErrorMessage } from '@shared/utils';
import {
  IBranchListItem,
  IBranchDetail,
  IBranchFormValues,
  IBranchSelect,
  IBranchTransferExecuteValues,
  IBranchTransferPreview,
  IBranchTransferSummary,
  IBranchWithDeliveryPoint,
  ICarrierLocation,
} from '../interfaces';
import { BranchFormErrorsMapper, BranchFormMapper, BranchMapper, BranchTransferMapper, BranchWithDeliveryPointMapper, CarrierLocationMapper } from '../mappers';

export const BranchesService = {
  getList: async (query: Record<string, any> = {}): Promise<ApiResult<200, { data: IBranchListItem[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/branches', query);
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data = (result.data || []).map(BranchMapper.toDomain);
        return new ApiResult(200, { data, total: result.total }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi', null);
    }
  },

  getSelect: async (includeDeleted?: boolean): Promise<ApiResult<200, IBranchSelect[]> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/branches/select', { include_deleted: includeDeleted });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, (result.data || []).map(BranchMapper.selectToDomain), null);
      }
      return new ApiResult(400, 'Filiallar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi', null);
    }
  },

  getById: async (id: string | number): Promise<ApiResult<200, IBranchDetail> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/branches/info', { branch_id: id });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, BranchMapper.detailToDomain(result.data), null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  save: async (values: IBranchFormValues, id?: string | number): Promise<ApiResult<200, null> | ApiResult<422, Partial<Record<keyof IBranchFormValues, string>>> | ApiResult<400, string>> => {
    const url = id ? urlMaker('/api/admin/branches/edit') : urlMaker('/api/admin/branches/create');
    const body = new FormData();
    appendToFormData(BranchFormMapper.toPersistence(values, id), body);
    try {
      const response = await caller(url, { method: 'POST', body });
      if (response.ok) return new ApiResult(200, null, null);
      if (response.status === 400) {
        const result = await response.json();
        return new ApiResult(422, BranchFormErrorsMapper.toDomain(result.errors || {}), null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  delete: async (id: string | number): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/branches/cancel', { branch_id: id });
    try {
      const response = await caller(url, { method: 'POST' });
      if (response.ok) return new ApiResult(200, null, null);
      if (response.status === 400 || response.status === 422) {
        const result = await response.json();
        return new ApiResult(
          400,
          Object.values(result.errors || {})
            .flat()
            .join('. '),
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },

  getCarrierLocations: async (slug: string): Promise<ApiResult<200, ICarrierLocation[]> | ApiResult<400, string>> => {
    const url = urlMaker(`/api/admin/carrier/${slug}/locations`);
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, (result.data || []).map(CarrierLocationMapper.toDomain), null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi', null);
    }
  },

  getAzerpostBranches: async (query: Record<string, any> = {}): Promise<ApiResult<200, { data: IBranchListItem[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/azerpost/branches', { page: 1, per_page: 5000, ...query });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data = (result.data || []).map(BranchMapper.toDomain);
        return new ApiResult(200, { data, total: result.total }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi', null);
    }
  },

  getBranchesWithDeliveryPoints: async (query: Record<string, any> = {}): Promise<ApiResult<200, { data: IBranchWithDeliveryPoint[]; total: number }> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/branches', { page: 1, per_page: 5000, ...query });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data: IBranchWithDeliveryPoint[] = (result.data || []).map(BranchWithDeliveryPointMapper.toDomain);
        return new ApiResult(200, { data, total: result.total }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi', null);
    }
  },

  createBranchesFromLocations: async (locationIds: (string | number)[], slug: string): Promise<ApiResult<200, null> | ApiResult<400, string>> => {
    const url = urlMaker(`/api/admin/carrier/${slug}/create-from-locations`, { location_ids: locationIds });
    try {
      const response = await caller(url, { method: 'POST' });
      if (response.ok) return new ApiResult(200, null, null);
      if (response.status === 400 || response.status === 422) {
        const result = await response.json();
        return new ApiResult(
          400,
          Object.values(result.errors || {})
            .flat()
            .join('. '),
          null,
        );
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },
  getTransferPreview: async (fromBranchId: number, toBranchId: number): Promise<ApiResult<200, IBranchTransferPreview> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/branches/transfer/preview', { from_branch_id: fromBranchId, to_branch_id: toBranchId });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, BranchTransferMapper.previewToDomain(result.data), null);
      }
      return new ApiResult(400, await extractErrorMessage(response, 'Önbaxış əldə edilə bilmədi'), null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi', null);
    }
  },

  executeTransfer: async (values: IBranchTransferExecuteValues): Promise<ApiResult<200, IBranchTransferSummary> | ApiResult<400, string>> => {
    const url = urlMaker('/api/admin/branches/transfer/execute');
    const body = JSON.stringify(BranchTransferMapper.executeToPersistence(values));
    try {
      const response = await caller(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, BranchTransferMapper.summaryToDomain(result.data), null);
      }
      return new ApiResult(400, await extractErrorMessage(response, 'Köçürmə icra edilə bilmədi'), null);
    } catch {
      return new ApiResult(400, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  },
};
